import { useCustomer } from '@/app/context/CustomerContext/useCustomer';
import PageLayout from '../common/page-layout/PageLayout';
import { FeatureRow } from './feature-row/FeatureRow';
import { PurchasingOptions } from '../home-page/purchasing-options/PurchasingOptions';
import { PageSEO } from '../common/blog/elements/SEO';
import Link from 'next/link';
import { useAuth } from '@/app/context/AuthContext/useAuth';
import { useRouter } from 'next/router';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { useEffect, useState } from 'react';

export const Pricing = () => {
  const { isPro, isProEligible, country } = useCustomer();

  const { user, showAuth } = useAuth();

  const { trackEvent, analyticsInstance } = useAnalytics();

  const [didTrackUpgrade, setDidTrackUpgrade] = useState(false);

  const router = useRouter();

  useEffect(() => {
    if (!isPro && isProEligible && !didTrackUpgrade) {
      setDidTrackUpgrade(true);
      trackEvent(analyticsInstance, 'upgrade_visible', {
        upgrade_source: 'pricing'
      });
    }
  }, [isPro, isProEligible]);

  const signUp = () => {
    trackEvent(analyticsInstance, 'pricing_click_sign_up');

    if (!user || user.isAnonymous) {
      showAuth(true, {
        pathname: '/app'
      });
    } else {
      router.push('/app/account');
    }
  };

  const upgrade = () => {
    trackEvent(analyticsInstance, 'upgrade_click', {
      upgrade_source: 'pricing'
    });

    trackEvent(analyticsInstance, 'pricing_click_upgrade');

    if (!user || user.isAnonymous) {
      showAuth(true, {
        pathname: '/app/account',
        query: {
          interval: 'month',
          source: 'pricing'
        }
      });
    } else {
      router.push('/app/account?source=pricing&interval=month');
    }
  };

  return (
    <PageLayout>
      <>
        <PageSEO
          title={'Pricing - Limbiks'}
          description={'Pricing and Features for Limbiks'}
        />{' '}
        <section className="bg-base-100 pt-16 md:pt-20">
          <div className="mx-auto mt-6 overflow-x-auto bg-white p-6">
            {/* <h2 className="mb-4 text-3xl font-semibold">
              Pricing and Features
            </h2>
            {!isPro && isProEligible && (
              <div
                onClick={upgrade}
                className={`mt-4 inline-flex w-48 transform cursor-pointer items-center justify-center rounded bg-primary py-2 px-4 font-bold text-primary-content transition duration-300 ease-in-out hover:scale-105 hover:underline`}
              >
                Subscribe to Pro
              </div>
            )} */}

            <div className="bg-white py-24 sm:py-32">
              <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                  <h2 className="text-base font-semibold leading-7 text-primary">
                    Pricing
                  </h2>
                  <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                    See what our plans have to offer
                  </p>
                </div>
                <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
                  Limbiks offers a limited set of features for free. Upgrade to
                  pro to unlock the full experience and help support Limbiks.
                </p>

                {/* xs to lg */}
                <div className="mx-auto mt-12 max-w-md space-y-8 sm:mt-16 lg:hidden">
                  {tiers.map((tier) => (
                    <section
                      key={tier.id}
                      className={classNames(
                        tier.mostPopular
                          ? 'rounded-xl bg-gray-400/5 ring-1 ring-inset ring-gray-200'
                          : '',
                        'p-8'
                      )}
                    >
                      <h3
                        id={tier.id}
                        className="text-sm font-semibold leading-6 text-gray-900"
                      >
                        {tier.name}
                      </h3>
                      <p className="mt-2 flex items-baseline gap-x-1 text-gray-900">
                        <span className="text-4xl font-bold">
                          {tier.priceMonthly}
                        </span>
                        <span className="text-sm font-semibold">/month</span>
                      </p>
                      <a
                        onClick={() => {
                          if (tier.mostPopular) {
                            upgrade();
                          } else {
                            signUp();
                          }
                        }}
                        aria-describedby={tier.id}
                        className={classNames(
                          tier.mostPopular
                            ? 'bg-primary text-white hover:opacity-80'
                            : 'text-primary ring-1 ring-inset ring-gray-200 hover:ring-gray-200',
                          'mt-8 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                        )}
                      >
                        {tier.mostPopular ? 'Subscribe' : 'Sign Up'}
                      </a>
                      <ul
                        role="list"
                        className="mt-10 space-y-4 text-sm leading-6 text-gray-900"
                      >
                        {sections.map((section) => (
                          <li key={section.name}>
                            <ul role="list" className="space-y-4">
                              {section.features.map((feature) =>
                                feature.tiers[tier.name] ? (
                                  <li
                                    key={feature.name}
                                    className="flex gap-x-3"
                                  >
                                    <CheckIcon
                                      aria-hidden="true"
                                      className="h-6 w-5 flex-none text-indigo-600"
                                    />
                                    <span>
                                      {feature.name}{' '}
                                      {typeof feature.tiers[tier.name] ===
                                      'string' ? (
                                        <span className="text-sm leading-6 text-gray-500">
                                          ({feature.tiers[tier.name]})
                                        </span>
                                      ) : null}
                                    </span>
                                  </li>
                                ) : null
                              )}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>

                {/* lg+ */}
                <div className="isolate mt-20 hidden lg:block">
                  <div className="relative -mx-8">
                    {tiers.some((tier) => tier.mostPopular) ? (
                      <div className="absolute inset-x-4 inset-y-0 -z-10 flex">
                        <div
                          style={{
                            marginLeft: `72%`
                          }}
                          aria-hidden="true"
                          className="flex w-1/4 pl-4"
                        >
                          <div className="w-full rounded-t-xl border-x border-t border-gray-900/10 bg-gray-400/5" />
                        </div>
                      </div>
                    ) : null}
                    <table className="w-full table-fixed border-separate border-spacing-x-8 text-left">
                      <caption className="sr-only">
                        Pricing plan comparison
                      </caption>
                      <colgroup>
                        <col className="w-1/2" />
                        <col className="w-1/4" />
                        <col className="w-1/4" />
                      </colgroup>
                      <thead>
                        <tr>
                          <td />
                          {tiers.map((tier) => (
                            <th
                              key={tier.id}
                              scope="col"
                              className="px-6 pt-6 xl:px-8 xl:pt-8"
                            >
                              <div className="text-sm font-semibold leading-7 text-gray-900">
                                {tier.name}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th scope="row">
                            <span className="sr-only">Price</span>
                          </th>
                          {tiers.map((tier) => (
                            <td key={tier.id} className="px-6 pt-2 xl:px-8">
                              <div className="flex items-baseline gap-x-1 text-gray-900">
                                <span className="text-4xl font-bold">
                                  {tier.priceMonthly}
                                </span>
                                <span className="text-sm font-semibold leading-6">
                                  /month
                                </span>
                              </div>
                              <a
                                onClick={() => {
                                  if (tier.mostPopular) {
                                    upgrade();
                                  } else {
                                    signUp();
                                  }
                                }}
                                className={classNames(
                                  (tier.mostPopular &&
                                    isProEligible &&
                                    !isPro) ||
                                    !user
                                    ? 'visible'
                                    : 'invisible',
                                  tier.mostPopular
                                    ? 'bg-primary text-white hover:opacity-80'
                                    : 'text-gray-600 ring-1 ring-inset ring-gray-200 hover:ring-gray-300',
                                  'mt-8 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                )}
                              >
                                {tier.mostPopular ? 'Subscribe' : 'Sign Up'}
                              </a>
                            </td>
                          ))}
                        </tr>
                        {sections.map((section, sectionIdx) => (
                          <Fragment key={section.name}>
                            <tr>
                              <th
                                scope="colgroup"
                                colSpan={4}
                                className={classNames(
                                  sectionIdx === 0 ? 'pt-8' : 'pt-16',
                                  'pb-4 text-sm font-semibold leading-6 text-gray-900'
                                )}
                              >
                                <div className="flex">
                                  {section.name}{' '}
                                  {/* {section.link && (
                                    <ArrowTopRightOnSquareIcon className="mx-auto ml-3 h-5 w-5 text-gray-900" />
                                  )} */}
                                  <div className="flex-1"></div>
                                </div>
                                <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/10" />
                              </th>
                            </tr>
                            {section.features.map((feature) => (
                              <tr key={feature.name}>
                                <th
                                  scope="row"
                                  className="py-4 text-sm font-normal leading-6 text-gray-900"
                                >
                                  <div className="flex">
                                    {feature.name}{' '}
                                    {/* {section.link && (
                                    <ArrowTopRightOnSquareIcon className="mx-auto ml-3 h-5 w-5 text-gray-900" />
                                  )} */}
                                    <div className="flex-1"></div>
                                  </div>

                                  <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/5" />
                                </th>
                                {tiers.map((tier) => (
                                  <td
                                    key={tier.id}
                                    className="px-6 py-4 xl:px-8"
                                  >
                                    {typeof feature.tiers[tier.name] ===
                                    'string' ? (
                                      <div className="text-center text-sm leading-6 text-gray-500">
                                        {feature.tiers[tier.name]}
                                      </div>
                                    ) : (
                                      <>
                                        {feature.tiers[tier.name] === true ? (
                                          <CheckIcon
                                            aria-hidden="true"
                                            className="mx-auto h-5 w-5 text-indigo-600"
                                          />
                                        ) : (
                                          <MinusIcon
                                            aria-hidden="true"
                                            className="mx-auto h-5 w-5 text-gray-400"
                                          />
                                        )}

                                        <span className="sr-only">
                                          {feature.tiers[tier.name] === true
                                            ? 'Included'
                                            : 'Not included'}{' '}
                                          in {tier.name}
                                        </span>
                                      </>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* 
            <table className="w-full">
              <caption className="sr-only">Pricing plan comparison</caption>
              <colgroup>
                <col />
                <col />
                <col className="bg-base-300" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col">
                    <h2 className="mt-3 px-2 text-lg font-medium">Free</h2>
                    <p className="mb-3">
                      <span className="text-2xl font-bold sm:text-4xl">
                        {country.currencySymbol}0
                      </span>
                      <span className="font-medium"> / mo</span>
                    </p>
                  </th>
                  <th scope="col">
                    <h2 className="text-s mt-3 px-2 text-lg font-medium">
                      Pro
                    </h2>
                    <p className="mb-3">
                      <span className="text-2xl font-bold sm:text-4xl">
                        {country.currencySymbol}
                        {country.monthlyRate}
                      </span>
                      <span className="font-medium"> / mo</span>
                    </p>
                  </th>
                </tr>
              </thead>
              <tbody className="space-y-6 divide-y divide-gray-700 text-center">
                <FeatureRow
                  name={'Uploads / Generations per month'}
                  description={
                    'Upload and generate from PDFs, Powerpoints, word documents, images and text files'
                  }
                  freeText="10"
                  proText="100"
                ></FeatureRow>
                <FeatureRow
                  name={'Pages / Slides per upload'}
                  description={
                    'Choose exactly which pages and slides to generate from'
                  }
                  freeText="5"
                  proText="200"
                ></FeatureRow>
                <FeatureRow
                  name={'Generate from YouTube'}
                  description={
                    'Paste the link to any YouTube video with closed captioning'
                  }
                ></FeatureRow>
                <FeatureRow
                  name={'Generate from Wikipedia'}
                  description="Quickly search and generate from any Wikipedia article"
                ></FeatureRow>
                <FeatureRow
                  name={'Flashcard Study Tool'}
                  description={
                    'Review, shuffle, and edit all of your flashcards'
                  }
                ></FeatureRow>
                <FeatureRow
                  name={'Learning Objectives'}
                  description={
                    'Filter out flashcards automatically based on your learning objectives'
                  }
                  isProOnly
                ></FeatureRow>
                <FeatureRow
                  name={'Custom Instructions'}
                  description={
                    'Provide custom instructions to Limbiks about how you want your flashcards'
                  }
                  isProOnly
                ></FeatureRow>
                <FeatureRow
                  name={'AI Generated Study Guides'}
                  description={
                    'Easily review your uploads. Download study guides as word documents or pdfs'
                  }
                  isProOnly
                ></FeatureRow>
                <FeatureRow
                  name={'Practice Tests per month'}
                  description={
                    'Generate practice tests with score reports from all of your decks'
                  }
                  freeText="10"
                  proText="Unlimited"
                ></FeatureRow>
                <FeatureRow
                  name={'Questions per Practice Test'}
                  description={
                    'Select how many questions you want for each practice test (cannot exceed total # of flashcards)'
                  }
                  freeText="10"
                  proText="100"
                ></FeatureRow>
                <FeatureRow
                  name={'Hints and Explanations'}
                  description={'Get hints and explanations for every flashcard'}
                  isProOnly
                ></FeatureRow>
                <FeatureRow
                  name={'Multiple Choice Questions per month'}
                  description={
                    'Study any flashcard as a multiple choice question'
                  }
                  freeText="100"
                  proText="Unlimited"
                ></FeatureRow>
                <FeatureRow
                  name={'Limit # of Flashcards Generated'}
                  description="Tell Limbiks exactly how many flashcards you want, or generate all available"
                ></FeatureRow>
                <FeatureRow
                  name={'Download Flashcards'}
                  description="Download a CSV File to import flashcards into another tool"
                ></FeatureRow>
                <FeatureRow
                  name={'Sync to Anki'}
                  description="One click sync to Anki with AnkiConnect"
                ></FeatureRow>
                <FeatureRow
                  name={'Share Decks'}
                  description={'Share decks with friends and classmates'}
                ></FeatureRow>
                <FeatureRow
                  name={'Dark / Light Mode'}
                  description={
                    'Choose a theme for Limbiks that best suits your style and your eyes'
                  }
                ></FeatureRow>
              </tbody>
            </table> */}
          </div>
        </section>
      </>
    </PageLayout>
  );
};

import { Fragment } from 'react';
import { CheckIcon, MinusIcon } from '@heroicons/react/20/solid';
import {
  ArrowTopRightOnSquareIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

const tiers = [
  {
    name: 'Free',
    id: 'tier-free',
    href: '#',
    priceMonthly: '$0',
    description: 'Free plan.',
    mostPopular: false
  },
  {
    name: 'Pro',
    id: 'tier-pro',
    href: '#',
    priceMonthly: '$5',
    description: 'Pro plan.',
    mostPopular: true
  }
];
const sections = [
  {
    name: 'Generate',
    features: [
      {
        name: 'Number of Uploads / Generations',
        tiers: { Free: '10 / month', Pro: '100 / month', Premium: true }
      },
      {
        name: 'Pages and slides per upload',
        tiers: { Free: '5', Pro: '200', Premium: true }
      },
      {
        name: 'Add Learning Objectives',
        tiers: { Free: false, Pro: true, Premium: true }
      },
      {
        name: 'Add Custom Instructions',
        tiers: { Free: false, Pro: true, Premium: true }
      }
    ]
  },
  {
    name: 'Features',
    features: [
      {
        name: 'Flashcards Study Tool',
        tiers: { Free: true, Pro: true }
      },
      {
        name: 'Spaced repetition',
        link: '#',
        tiers: { Free: true, Pro: true }
      },
      {
        name: 'Download Flashcards',
        link: '#',
        tiers: { Free: true, Pro: true, Premium: true }
      },
      {
        name: 'Sync to Anki',
        link: '#',
        tiers: { Free: true, Pro: true }
      },
      {
        name: 'Multiple Choice Questions',
        link: '#',
        tiers: { Free: '100 / month', Pro: 'Unlimited' }
      },
      {
        name: 'AI Generated Study Guides',
        link: '#',
        tiers: { Free: false, Pro: true }
      },
      {
        name: 'Practice Tests',
        link: '#',
        tiers: { Free: '10 / month', Pro: 'Unlimited' }
      },
      {
        name: 'Questions per Practice Test',
        tiers: { Free: '10', Pro: '100' }
      },
      {
        name: 'Hints and Explanations',
        link: '#',
        tiers: { Free: false, Pro: true }
      }
    ]
  },
  {
    name: 'AI Image Occlusion Anki Add-on',
    link: '#',
    features: [
      {
        name: 'Number of Generations',
        tiers: { Free: '25 / month', Pro: '500 / month' }
      }
    ]
  }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export const Pricing2 = () => {
  return (
    <div className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-base font-semibold leading-7 text-indigo-600">
            Pricing
          </h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Plans for teams of&nbsp;all&nbsp;sizes
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
          Distinctio et nulla eum soluta et neque labore quibusdam. Saepe et
          quasi iusto modi velit ut non voluptas in. Explicabo id ut laborum.
        </p>

        {/* xs to lg */}
        <div className="mx-auto mt-12 max-w-md space-y-8 sm:mt-16 lg:hidden">
          {tiers.map((tier) => (
            <section
              key={tier.id}
              className={classNames(
                tier.mostPopular
                  ? 'rounded-xl bg-gray-400/5 ring-1 ring-inset ring-gray-200'
                  : '',
                'p-8'
              )}
            >
              <h3
                id={tier.id}
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                {tier.name}
              </h3>
              <p className="mt-2 flex items-baseline gap-x-1 text-gray-900">
                <span className="text-4xl font-bold">{tier.priceMonthly}</span>
                <span className="text-sm font-semibold">/month</span>
              </p>
              <a
                href={tier.href}
                aria-describedby={tier.id}
                className={classNames(
                  tier.mostPopular
                    ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                    : 'text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300',
                  'mt-8 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                )}
              >
                Buy plan
              </a>
              <ul
                role="list"
                className="mt-10 space-y-4 text-sm leading-6 text-gray-900"
              >
                {sections.map((section) => (
                  <li key={section.name}>
                    <ul role="list" className="space-y-4">
                      {section.features.map((feature) =>
                        feature.tiers[tier.name] ? (
                          <li key={feature.name} className="flex gap-x-3">
                            <CheckIcon
                              aria-hidden="true"
                              className="h-6 w-5 flex-none text-indigo-600"
                            />
                            <span>
                              {feature.name}{' '}
                              {typeof feature.tiers[tier.name] === 'string' ? (
                                <span className="text-sm leading-6 text-gray-500">
                                  ({feature.tiers[tier.name]})
                                </span>
                              ) : null}
                            </span>
                          </li>
                        ) : null
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* lg+ */}
        <div className="isolate mt-20 hidden lg:block">
          <div className="relative -mx-8">
            {tiers.some((tier) => tier.mostPopular) ? (
              <div className="absolute inset-x-4 inset-y-0 -z-10 flex">
                <div
                  style={{
                    marginLeft: `${
                      (tiers.findIndex((tier) => tier.mostPopular) + 2) * 25
                    }%`
                  }}
                  aria-hidden="true"
                  className="flex w-1/4 pl-4"
                >
                  <div className="w-full rounded-t-xl border-x border-t border-gray-900/10 bg-gray-400/5" />
                </div>
              </div>
            ) : null}
            <table className="w-full table-fixed border-separate border-spacing-x-8 text-left">
              <caption className="sr-only">Pricing plan comparison</caption>
              <colgroup>
                <col className="w-1/2" />
                <col className="w-1/4" />
                <col className="w-1/4" />
              </colgroup>
              <thead>
                <tr>
                  <td />
                  {tiers.map((tier) => (
                    <th
                      key={tier.id}
                      scope="col"
                      className="px-6 pt-6 xl:px-8 xl:pt-8"
                    >
                      <div className="text-sm font-semibold leading-7 text-gray-900">
                        {tier.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">
                    <span className="sr-only">Price</span>
                  </th>
                  {tiers.map((tier) => (
                    <td key={tier.id} className="px-6 pt-2 xl:px-8">
                      <div className="flex items-baseline gap-x-1 text-gray-900">
                        <span className="text-4xl font-bold">
                          {tier.priceMonthly}
                        </span>
                        <span className="text-sm font-semibold leading-6">
                          /month
                        </span>
                      </div>
                      <a
                        href={tier.href}
                        className={classNames(
                          tier.mostPopular
                            ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                            : 'text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300',
                          'mt-8 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                        )}
                      >
                        Buy plan
                      </a>
                    </td>
                  ))}
                </tr>
                {sections.map((section, sectionIdx) => (
                  <Fragment key={section.name}>
                    <tr>
                      <th
                        scope="colgroup"
                        colSpan={4}
                        className={classNames(
                          sectionIdx === 0 ? 'pt-8' : 'pt-16',
                          'pb-4 text-sm font-semibold leading-6 text-gray-900'
                        )}
                      >
                        {section.name}
                        <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/10" />
                      </th>
                    </tr>
                    {section.features.map((feature) => (
                      <tr key={feature.name}>
                        <th
                          scope="row"
                          className="py-4 text-sm font-normal leading-6 text-gray-900"
                        >
                          {feature.name}
                          <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/5" />
                        </th>
                        {tiers.map((tier) => (
                          <td key={tier.id} className="px-6 py-4 xl:px-8">
                            {typeof feature.tiers[tier.name] === 'string' ? (
                              <div className="text-center text-sm leading-6 text-gray-500">
                                {feature.tiers[tier.name]}
                              </div>
                            ) : (
                              <>
                                {feature.tiers[tier.name] === true ? (
                                  <CheckIcon
                                    aria-hidden="true"
                                    className="mx-auto h-5 w-5 text-indigo-600"
                                  />
                                ) : (
                                  <MinusIcon
                                    aria-hidden="true"
                                    className="mx-auto h-5 w-5 text-gray-400"
                                  />
                                )}

                                <span className="sr-only">
                                  {feature.tiers[tier.name] === true
                                    ? 'Included'
                                    : 'Not included'}{' '}
                                  in {tier.name}
                                </span>
                              </>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
